/**
 * recordingProfiles (Welle 7e / pre96) — Abstraktion für Paketierung und Ablage.
 *
 * Trennt „wie wir aufzeichnen" (kanonischer Record) von „was das Zielarchiv des
 * jeweiligen Kunden erwartet". Zwei orthogonale Achsen:
 *   - Export-Profil   → Container + Manifest + Verschlüsselung (Paketierung)
 *   - Delivery-Adapter → Zustellweg (Ablage)
 *
 * pre96 implementiert: Export `canonical` + `competitor-zip`, Delivery `folder` (FSA)
 * und `download`. Die übrigen sind als Deskriptoren vorhanden (typsicher, `implemented:false`)
 * und werden in folgenden Pre-Releases ergänzt (MP3-Transcode, GPG, SFTP/S3/Archiv-Konnektor).
 * Siehe Welle-7e-Aufzeichnung-Konzept.md.
 */

export type ExportProfileId = 'canonical' | 'competitor-zip' | 'asic-e' | 'tr-esor-xaip';
export type DeliveryAdapterId =
  | 'folder' | 'download' | 'sftp' | 'http-api' | 's3' | 'archive-connector' | 'email' | 'companion';

export type ContainerKind = 'zip' | 'asic-e' | 'xaip';
export type ManifestKind = 'canonical' | 'competitor-info-xml';
export type EncryptionKind = 'none' | 'openpgp' | 'cms' | 'age' | 'kms';

export interface ExportProfile {
  id: ExportProfileId;
  label: string;
  container: ContainerKind;
  manifest: ManifestKind;
  /** zusätzlich ein menschenlesbares Dialogprotokoll beilegen */
  attachProtocol: boolean;
  /** Verschlüsselung des Pakets (pre96: nur 'none'; GPG/KMS folgen) */
  encryption: EncryptionKind;
  implemented: boolean;
  note?: string;
}

export interface DeliveryAdapter {
  id: DeliveryAdapterId;
  label: string;
  /** liefert in ein revisionssicheres Endlager (WORM/Archiv) — sonst nur Transfer */
  compliantSink: boolean;
  implemented: boolean;
  note?: string;
}

export const EXPORT_PROFILES: Record<ExportProfileId, ExportProfile> = {
  'canonical': {
    id: 'canonical', label: 'Kanonisch (Bamoa, schema-valide)',
    container: 'zip', manifest: 'canonical', attachProtocol: true,
    encryption: 'none', implemented: true,
  },
  'competitor-zip': {
    id: 'competitor-zip', label: 'Kompatibel zum Markt-Paket (info.xml + ZIP)',
    container: 'zip', manifest: 'competitor-info-xml', attachProtocol: true,
    encryption: 'none', implemented: true,
    note: 'Layout des analysierten Beispielpakets. MP3-Transcode + GPG (.gpg) folgen serverseitig.',
  },
  'asic-e': {
    id: 'asic-e', label: 'ASiC-E (ETSI EN 319 162)',
    container: 'asic-e', manifest: 'canonical', attachProtocol: true,
    encryption: 'cms', implemented: false, note: 'Folgt: standardisierter Container.',
  },
  'tr-esor-xaip': {
    id: 'tr-esor-xaip', label: 'TR-ESOR / (L)XAIP (BSI TR-03125)',
    container: 'xaip', manifest: 'canonical', attachProtocol: true,
    encryption: 'cms', implemented: false, note: 'Folgt: Anschluss an TR-ESOR-Bewahrungsdienst.',
  },
};

export const DELIVERY_ADAPTERS: Record<DeliveryAdapterId, DeliveryAdapter> = {
  'folder': {
    id: 'folder', label: 'Ordner-Ablage (File System Access API)',
    compliantSink: false, implemented: true,
    note: 'FI-Hot-Folder: Paket landet im zentralen Eingangsordner und wird dort weiterverarbeitet. Kein revisionssicheres Endlager an sich.',
  },
  'download': {
    id: 'download', label: 'Manueller Download / Speichern unter',
    compliantSink: false, implemented: true,
  },
  'sftp': { id: 'sftp', label: 'SFTP / FTPS', compliantSink: false, implemented: false, note: 'Folgt (serverseitig).' },
  'http-api': { id: 'http-api', label: 'HTTP/REST-Ingest', compliantSink: false, implemented: false, note: 'Folgt (serverseitig).' },
  's3': { id: 's3', label: 'S3 / Object-Store (WORM)', compliantSink: true, implemented: false, note: 'Folgt (serverseitig, Object-Lock).' },
  'archive-connector': { id: 'archive-connector', label: 'Archiv-Konnektor (OSPlus/ECM/TR-ESOR)', compliantSink: true, implemented: false, note: 'Folgt (serverseitig).' },
  'email': { id: 'email', label: 'Sicheres Postfach / E-Mail', compliantSink: false, implemented: false, note: 'Folgt (serverseitig).' },
  'companion': {
    id: 'companion', label: 'Companion-Übergabe (P2P zum Terminal-Server)',
    compliantSink: false, implemented: true,
    note: 'Direkter P2P-DataChannel an die Companion-Sitzung auf dem TS; das Paket verlässt nie den Server. Dort greift die konfigurierte Ordner-Ablage.',
  },
};

export const DEFAULT_EXPORT_PROFILE: ExportProfileId = 'competitor-zip';
export const DEFAULT_DELIVERY: DeliveryAdapterId = 'folder';
